import { getCurrentPosition } from '../helpers.js'

export class GoogleMapUtils {
  static mapInstance = null
  static mapContainer = null
  static directionsService = null
  static directionsRenderer = null
  static userMarker = null
  static destinationMarker = null
  static apiKey = null
  static autocompleteService = null
  static placesService = null

  // ---- fonctions utilitaires de cache ----
  static _generateCacheKey(start, end, checkpoints = []) {
    const cpStr = checkpoints.map(p => `${p.lat},${p.lng}`).join('|')
    return `route_${start.lat},${start.lng}_${end.lat},${end.lng}_${cpStr}`
  }

  static _getCachedRoute(start, end, checkpoints) {
    try {
      const key = this._generateCacheKey(start, end, checkpoints)
      const json = localStorage.getItem(key)
      return json ? JSON.parse(json) : null
    } catch (e) {
      console.warn('Erreur cache localStorage', e)
      return null
    }
  }

  static _setCachedRoute(start, end, checkpoints, routeData) {
    try {
      const key = this._generateCacheKey(start, end, checkpoints)
      localStorage.setItem(key, JSON.stringify(routeData))
    } catch (e) {
      console.warn('Impossible de stocker dans localStorage', e)
    }
  }

  // ---- init et centre sur utilisateur ----
  static async init(elementId, options = {}, apiKey) {
    this.apiKey = apiKey || this.apiKey
    if (!this.apiKey) throw new Error('Google Maps API key manquante')

    const container = document.getElementById(elementId)
    if (!container) throw new Error(`Élément #${elementId} introuvable`)

    if (!window.google || !window.google.maps) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}`
        script.async = true
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    }

    this.initGooglePlaces()

    if (this.mapInstance) {
      console.log('♻️ Réutilisation de la carte existante')
      this.mapContainer = container
      container.appendChild(this.mapInstance.getDiv())
      google.maps.event.trigger(this.mapInstance, 'resize')
      return this.mapInstance
    }

    console.log('🆕 Création d’une nouvelle carte Google')
    const map = new google.maps.Map(container, {
      zoom: 14,
      center: { lat: 6.37, lng: 2.39 },
      disableDefaultUI: true,
      ...options,
    })

    this.mapInstance = map
    this.mapContainer = container
    this.directionsService = new google.maps.DirectionsService()
    this.directionsRenderer = new google.maps.DirectionsRenderer({ map })
    return map
  }

  static initGooglePlaces() {
    if (this.autocompleteService || this.placesService) return
    this.autocompleteService = new google.maps.places.AutocompleteService()
    this.placesService = new google.maps.places.PlacesService(
      document.createElement('div'),
    )
  }

  static async searchGooglePlaces(q) {
    if (!q || q.length < 3) {
      return []
    }
    console.log(
      '🚀 ~ GoogleMapUtils ~ searchGooglePlaces ~ this.autocompleteService:',
      this.autocompleteService,
    )
    return new Promise(resolve => {
      this.autocompleteService.getPlacePredictions(
        {
          input: q,
          componentRestrictions: { country: 'bj' },
          location: new google.maps.LatLng(6.3654, 2.4183), // Cotonou
          radius: 50000,
          types: ['establishment', 'geocode'],
        },
        (predictions, status) => {
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !predictions
          ) {
            resolve([])
            return
          }

          // 🔁 Mapping Google → Nominatim-like
          const suggestions = predictions.map(p => ({
            place_id: p.place_id,
            lat: null, // récupéré plus tard
            lon: null,
            class: 'place',
            type: p.types?.[0] || 'unknown',
            name: p.structured_formatting?.main_text,
            display_name: p.description,
            address: {
              label: p.description,
            },
            boundingbox: null,
            isGoogleLocationSuggest: true, // flag interne utile
          }))

          resolve(suggestions)
        },
      )
    })
  }

  static async resolveGooglePlace(suggestion) {
    return new Promise((resolve, reject) => {
      this.placesService.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ['geometry', 'address_components', 'formatted_address'],
        },
        (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            reject(status)
            return
          }

          resolve({
            ...suggestion,
            lat: place.geometry.location.lat(),
            lon: place.geometry.location.lng(),
            display_name: place.formatted_address,
          })
        },
      )
    })
  }

  static initFromVue(mapInstance) {
    if (!mapInstance) throw new Error('mapInstance manquante')
    this.mapInstance = mapInstance
    this.directionsService = new google.maps.DirectionsService()
    this.directionsRenderer = new google.maps.DirectionsRenderer({
      map: this.mapInstance,
    })
  }

  static async centerOnUser(getPosition = getCurrentPosition) {
    const position = await getPosition()
    if (!this.mapInstance) throw new Error('Map non initialisée')

    if (!this.userMarker) {
      this.userMarker = new google.maps.Marker({
        position,
        map: this.mapInstance,
        icon: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
        title: 'Votre position',
      })
    } else {
      this.userMarker.setPosition(position)
    }

    this.mapInstance.panTo(position)
  }

  // ---- traceRoute avec cache ----
  static async traceRoute(
    start,
    end,
    checkpoints = [],
    returnOnlyPath = false,
  ) {
    if (!this.directionsService)
      throw new Error('DirectionsService non initialisé')

    // 🔹 Vérifier cache
    const cached = this._getCachedRoute(start, end, checkpoints)
    if (cached) {
      console.log('♻️ Route trouvée dans le cache')
      if (!returnOnlyPath && this.directionsRenderer) {
        // si renderer actif, il faudra éventuellement refaire l'affichage
        // mais on ne refait pas la requête
      }
      return cached
    }

    // Google Maps limite à 10 waypoints maximum
    const maxWaypoints = 10
    let validWaypoints = checkpoints.map(point => ({
      location: new google.maps.LatLng(point.lat, point.lng),
      stopover: true,
    }))

    if (validWaypoints.length > maxWaypoints) {
      validWaypoints = validWaypoints.slice(0, maxWaypoints)
    }

    // Convertir aussi start et end en format LatLng
    const startLatLng = new google.maps.LatLng(start.lat, start.lng)
    const endLatLng = new google.maps.LatLng(end.lat, end.lng)

    if (!returnOnlyPath) {
      if (!this.destinationMarker) {
        this.destinationMarker = new google.maps.Marker({
          position: endLatLng,
          map: this.mapInstance,
          icon: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
          title: 'Destination',
        })
      } else {
        this.destinationMarker.setPosition(endLatLng)
      }
    }

    const request = {
      origin: startLatLng,
      destination: endLatLng,
      waypoints: validWaypoints,
      travelMode: google.maps.TravelMode.DRIVING,
      language: 'fr',
    }

    return new Promise((resolve, reject) => {
      this.directionsService.route(request, (result, status) => {
        if (status === 'OK') {
          if (!returnOnlyPath && this.directionsRenderer) {
            this.directionsRenderer.setDirections(result)
          }

          const route = result.routes[0].legs
          const routeData = route.map(leg => ({
            start: {
              lat: leg.start_location.lat(),
              lng: leg.start_location.lng(),
            },
            end: { lat: leg.end_location.lat(), lng: leg.end_location.lng() },
            distance: leg.distance.text,
            duration: leg.duration.text,
            steps: leg.steps.map(s => ({
              start: {
                lat: s.start_location.lat(),
                lng: s.start_location.lng(),
              },
              end: { lat: s.end_location.lat(), lng: s.end_location.lng() },
              instruction: s.instructions,
              detailSteps: s.path.map(p => ({ lat: p.lat(), lng: p.lng() })),
            })),
          }))

          // 🔹 Stocker dans cache
          this._setCachedRoute(start, end, checkpoints, routeData)

          resolve(routeData)
        } else {
          reject(
            new Error('Impossible de tracer l’itinéraire Google : ' + status),
          )
        }
      })
    })
  }

  // ---- reste des méthodes inchangées ----
  static async getCurrentAddress(position) {
    if (!position) position = await getCurrentPosition()
    const geocoder = new google.maps.Geocoder()
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results.length > 0)
          resolve(results[0].formatted_address)
        else reject(new Error('Adresse introuvable via Google'))
      })
    })
  }

  static async getGeocodeDetails(position) {
    if (!position) position = await getCurrentPosition()
    const geocoder = new google.maps.Geocoder()
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results.length > 0)
          resolve(results[0])
        else reject(new Error('Détails introuvables via Google'))
      })
    })
  }

  static detachMap() {
    if (!this.mapInstance || !this.mapContainer) return
    const mapDiv = this.mapInstance.getDiv()
    if (mapDiv?.parentNode) mapDiv.parentNode.removeChild(mapDiv)
  }

  static destroyMap() {
    if (!this.mapInstance) return
    this.mapInstance = null
    this.mapContainer = null
    this.directionsService = null
    this.directionsRenderer = null
    this.userMarker = null
    this.destinationMarker = null
  }
}
